<script setup>
import { i18n } from 'boot/i18n';
import { computed } from 'vue';

/**
 * A coverage's place in the coordination-of-benefits order.
 *
 * Two states:
 *
 *  - `numbered`    — we are billing on this coverage. A filled chip: "1" is billed first.
 *  - `placeholder` — we are not billing on this coverage: upcoming, on hold, or eligibility
 *                    unverified. Holds the space, claims nothing.
 *
 * There is no uncertain or tentative form. The arrangement of the list IS the order — dragging is
 * how a user sets it — so a position is never in a state of being unconfirmed.
 *
 * Local to this context rather than a design-system component: the design system exposes no
 * passive chip to app code, and a COB order badge is revenue-cycle vocabulary, not a
 * general-purpose primitive.
 */
defineOptions({ name: 'CoverageRankChip' });

const props = defineProps({
  /**
   * Which state to render.
   * @values numbered, placeholder
   */
  variant: {
    type: String,
    default: 'placeholder',
    validator: (v) => ['numbered', 'placeholder'].includes(v),
  },
  /** 1-based billing-order number. Required when `variant` is `numbered`. */
  number: {
    type: Number,
    default: null,
  },
});

const isNumbered = computed(() => props.variant === 'numbered');

const text = computed(() => (isNumbered.value ? `${props.number}` : '–'));

const ariaLabel = computed(() => {
  return isNumbered.value
    ? i18n.t('coverage.rankLabel', { rank: props.number })
    : i18n.t('coverage.rankPlaceholderLabel');
});
</script>

<template>
  <span
    class="coverage-rank-chip hr-label-medium"
    :class="`coverage-rank-chip--${variant}`"
    :aria-label="ariaLabel"
    :data-e2e="`coverage-rank-${variant}`"
  >
    {{ text }}
  </span>
</template>

<style lang="scss" scoped>
.coverage-rank-chip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  /* Never absorb the row's flex pressure — a squashed rank is an unreadable rank. */
  flex: 0 0 auto;
  min-width: 24px;
  height: 24px;
  padding: 0 6px;
  border: 2px solid transparent;
  border-radius: 8px;
  font-variant-numeric: tabular-nums;
}

/* We are billing on this one, in this position. */
.coverage-rank-chip--numbered {
  background: var(--app-primary-container);
  color: var(--app-on-primary-container);
}

/* No number to state. The quiet one. */
.coverage-rank-chip--placeholder {
  border-color: var(--app-outline-variant);
  border-width: 1px;
  background: transparent;
  color: var(--app-on-surface-variant);
}
</style>
